// Enums, stage definitions and label helpers shared by server and browser.

// Months a new client starts with (legacy: the old 24-month contract). Months go up to MAX_MONTH on demand.
export const MONTH_COUNT = 24;
// Month no. and Batch no. are 1–99 (DESIGN §7): a client's project can run past its contract.
export const MAX_MONTH = 99;
export const MAX_BATCH = 99;
// Videos a batch produces (Script / Video Editing / Result targets); editable 1–200 per batch (DESIGN §9).
export const DEFAULT_VIDEO_COUNT = 15;
export const MAX_VIDEO_COUNT = 200;
// The month selector shows at least this many months.
export const MIN_VISIBLE_MONTHS = 12;

// target: 'one' = 1 deliverable, 'videos' = the batch's videoCount, 'briefs' = 2 (Brief Videographer + Brief Video Editing).
export const STAGES = [
  { key: 'briefPremisScript',      number: '01', label: 'Brief Script',                             short: 'Brief Script', tool: 'Google Docs',  action: 'Open Document', urlField: 'briefPremisScriptUrl', target: 'one' },
  { key: 'script',                 number: '02', label: 'Script',                                   short: 'Script',       tool: 'Google Docs',  action: 'Open Document', urlField: 'scriptUrl', target: 'videos' },
  { key: 'videoShootEditingBrief', number: '03', label: 'Brief Videographer & Brief Video Editing', short: 'Briefs',       tool: 'Google Docs',  action: 'Open Document', urlField: 'videoShootEditingBriefUrl', target: 'briefs' },
  { key: 'videoEditing',           number: '04', label: 'Video Editing',                            short: 'Editing',      tool: 'Google Drive', action: 'Open Folder',   urlField: 'videoEditingUrl', target: 'videos' },
  { key: 'result',                 number: '05', label: 'Result',                                   short: 'Result',       tool: 'Google Drive', action: 'Open Folder',   urlField: 'resultUrl', target: 'videos' },
];

// Stage 03's two briefs, each with its own link and done checkmark.
export const BRIEFS = [
  { key: 'videographer', label: 'Brief Videographer', urlField: 'videoShootEditingBriefUrl' },
  { key: 'editing', label: 'Brief Video Editing', urlField: 'videoEditingBriefUrl' },
];
export const BRIEF_KEYS = BRIEFS.map((b) => b.key);

export const STAGE_KEYS = STAGES.map((s) => s.key);
export const URL_FIELDS = [...STAGES.map((s) => s.urlField), 'videoEditingBriefUrl'];

// Deliverables a stage needs before it is complete: Brief Script 1 · Script / Video Editing / Result = videoCount · Briefs 2.
export function videoCountOf(batch) {
  const n = Number(batch?.videoCount);
  return Number.isInteger(n) && n >= 1 && n <= MAX_VIDEO_COUNT ? n : DEFAULT_VIDEO_COUNT;
}
export function stageTarget(batch, stageKey) {
  const kind = STAGES.find((s) => s.key === stageKey)?.target;
  if (kind === 'briefs') return BRIEFS.length;
  if (kind === 'videos') return videoCountOf(batch);
  return 1;
}

// Stage and batch statuses (DESIGN §0). A batch is Blocked when its current stage is Blocked.
export const STAGE_STATUSES = ['To Do', 'In Progress', 'Review', 'Blocked', 'Done'];
export const BATCH_STATUSES = STAGE_STATUSES;
// Unfinished work: every batch status except Done.
export const OPEN_STATUSES = ['To Do', 'In Progress', 'Review', 'Blocked'];
export const CLIENT_STATUSES = ['Active', 'Paused', 'Completed'];
export const MONTH_STATUSES = ['Upcoming', 'Active', 'Completed', 'Off'];

// Badge tone of a status: 'neutral' | 'accent' | 'warning' | 'danger' | 'positive'.
// kind: 'batch' (default; also stage statuses) | 'client' | 'month'. Pre-v2 'Not Started' reads as To Do.
const TONES = {
  batch: { 'To Do': 'neutral', 'Not Started': 'neutral', 'In Progress': 'accent', Review: 'warning', Blocked: 'danger', Done: 'positive' },
  client: { Active: 'accent', Paused: 'warning', Completed: 'neutral' },
  month: { Upcoming: 'neutral', Active: 'accent', Completed: 'positive', Off: 'danger' },
};
export function statusTone(status, kind = 'batch') {
  return (TONES[kind] || TONES.batch)[status] || TONES.batch[status] || TONES.client[status] || 'neutral';
}

// Calendars of stored events. 'posting' (DESIGN §9) is derived from the clients' posting days, never stored.
export const CALENDAR_TYPES = [
  { key: 'main', label: 'Main Calendar' },
  { key: 'internal', label: 'Internal Notes' },
];
export const POSTING_CALENDAR = { key: 'posting', label: 'Posting' };
export const CALENDAR_TOGGLES = [...CALENDAR_TYPES, POSTING_CALENDAR]; // MY CALENDARS checkboxes

// ---------- client contract (DESIGN §9) ----------
export const CONTRACT_BATCHES = { min: 1, max: 18, default: 1 };
export const CONTRACT_MONTHS = { min: 1, max: 24, default: 6 };
export const CONTENT_PER_MONTH = { min: 1, max: 200, default: 15 };
export const HANDLE_MAX = 30; // Instagram / TikTok handle, stored without '@': [A-Za-z0-9._]
export const WEEKDAYS = [
  { key: 'mon', label: 'Mon', long: 'Monday' }, { key: 'tue', label: 'Tue', long: 'Tuesday' },
  { key: 'wed', label: 'Wed', long: 'Wednesday' }, { key: 'thu', label: 'Thu', long: 'Thursday' },
  { key: 'fri', label: 'Fri', long: 'Friday' }, { key: 'sat', label: 'Sat', long: 'Saturday' },
  { key: 'sun', label: 'Sun', long: 'Sunday' },
];
export const WEEKDAY_KEYS = WEEKDAYS.map((d) => d.key);
// Client PIC roles and who holds each stage: Brief Script + Script → script; Brief Videographer → videographer;
// Brief Video Editing, Video Editing, Result → editor. Stage 03 as a whole starts with its first brief (videographer).
export const PIC_ROLES = [
  { key: 'script', label: 'Script PIC' },
  { key: 'videographer', label: 'Videographer PIC' },
  { key: 'editor', label: 'Editor PIC' },
];
export const PIC_KEYS = PIC_ROLES.map((r) => r.key);
export const STAGE_PIC = { briefPremisScript: 'script', script: 'script', videoShootEditingBrief: 'videographer', videoEditing: 'editor', result: 'editor' };
export const BRIEF_PIC = { videographer: 'videographer', editing: 'editor' };

export const EVENT_KINDS = {
  main: ['Script Deadline', 'Shoot Date', 'Editing Deadline', 'Result Deadline', 'Client Meeting', 'Production Meeting'],
  internal: ['Internal Reminder', 'Production Note', 'Team Note', 'Internal Deadline'],
};

// Performance ranges (default 30d); the period always ends today. bucket = chart grain:
// day → the last 7/30 days; week → the current week + 12/25 previous full weeks (Mon–Sun); month → current + 11 months.
export const PERFORMANCE_RANGES = [
  { key: '7d', label: '7D', long: 'Last 7 days', bucket: 'day' },
  { key: '30d', label: '30D', long: 'Last 30 days', bucket: 'day' },
  { key: '3m', label: '3M', long: 'Last 3 months', bucket: 'week' },
  { key: '6m', label: '6M', long: 'Last 6 months', bucket: 'week' },
  { key: '1y', label: '1Y', long: 'Last 12 months', bucket: 'month' },
];
export const DEFAULT_RANGE = '30d';

// Overview period dropdown (default today).
export const OVERVIEW_PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
];

export function stageByKey(key) {
  return STAGES.find((s) => s.key === key);
}

export function stageIndex(key) {
  return STAGE_KEYS.indexOf(key);
}

export function pad2(n) {
  return String(n).padStart(2, '0');
}

export function monthLabel(n) {
  return `Month ${pad2(n)}`;
}

export function batchLabel(n) {
  return `Batch ${pad2(n)}`;
}

// plural(1, 'batch', 'batches') → '1 batch'; plural(3, 'day') → '3 days'.
export function plural(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

// ---------- people (DESIGN §8) ----------

// Suggested options; both fields also accept free text.
export const TEAM_ROLES = ['Editor', 'Visual Director', 'CEO', 'Content Strategist', 'Project Manager', 'Head Creative'];
export const DEPARTMENTS = ['Video Editor', 'Creative', 'Leadership / Ops', 'Strategy'];
export const LOGIN_ID_PATTERN = /^[a-z0-9._-]{2,40}$/;
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 200;

// 'Bryant Tan' → 'bryant'; 'José' → 'jose'. May be shorter than 2 characters (callers fall back).
export function loginSlug(name) {
  const first = String(name ?? '').trim().split(/\s+/)[0] || '';
  return first.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 40);
}
